import type { BulleEnrichie, Etat, Priorite } from '../partage/api/types';
import { ceQuElleAttendSelon } from '../moteur/conditions';
import { PastilleJamaisOuverte, PointsDeContenu, PastilleEtat } from '../apparence';
import { useDeuxGestes } from '../partage/deuxGestes';
import { attributsDeLHabit, SilhouetteDObjet, type HabitDessine } from '../partage/objetsDeBulle';
import { MenuDeLHabit, type HabitDuProjetPourUneBulle } from '../fiche';
import type { PlaceDeCarte } from './placeDeLaHierarchie';
import { TitreSousLaLoupe } from '../partage/TitreSousLaLoupe';
import { classeDeLaLueur, DureeEnAnalyse, ReboursEnAnalyse } from '../partage/BulleEnAnalyse';
import type { LoupeSurUneBulle } from '../partage/loupeDeLaCarte';
import {
  DEUX_GESTES,
  etiquetteDuCalque,
  IDEE,
  majuscule,
  etatSuivant as suivant,
  titreDuCalque,
} from '../partage/vocabulaire';

/**
 * Une carte de la vue « Hiérarchie » (ticket 35).
 *
 * Elle dit d'un coup d'œil où la bulle en est et par où elle se situe dans ton ordre : son
 * rond d'état, son rang, son titre **en entier** (jamais coupé : la carte s'allonge), ce
 * qu'elle attend encore d'ailleurs en une ligne violette, et les petits points de ce qu'elle
 * contient. La flèche ronde de droite déplie ses bulles dans la colonne suivante.
 *
 * Les gestes sont ceux de la carte : un clic sur le titre entre dans la bulle, un double-clic
 * ouvre sa fiche, le rond change son état (valider passe par l'inventaire). Pendant qu'on
 * hiérarchise, seules les bulles de la feuille ouverte se désignent ; les étapes dépliées se
 * hiérarchisent en entrant dedans.
 */
export function NoeudHierarchie({
  bulle,
  place,
  axe,
  habit,
  habitDuProjet,
  dedans,
  deplie,
  neuf,
  mesure,
  enHierarchisation,
  designable,
  rangEnAttente,
  horsDuCalque = false,
  combienDansLeCalque = null,
  loupe,
  onEntrer,
  onFiche,
  onEtat,
  onDesigner,
  onDeplier,
}: {
  bulle: BulleEnrichie;
  place: PlaceDeCarte;
  /**
   * Sa couleur d'axe, `var(--axe-N)`, la même qu'en Carte (ticket 11 de retours-testeurs),
   * ou `null` pour une bulle que la carte ne connaît pas.
   */
  axe: string | null;
  /**
   * Son habit (chantier habit-des-bulles, modification 03) : sa forme ou son objet, son trait,
   * les siens sinon ceux du projet ; rien au téléphone.
   */
  habit: HabitDessine;
  /** L'habit du projet, pour son menu « ⋯ » (habit-des-bulles, modification 05) ; `null` : pas de menu. */
  habitDuProjet: HabitDuProjetPourUneBulle | null;
  /** Plus bas que le premier tour : la couleur est plus claire, comme sur la carte. */
  dedans: boolean;
  deplie: boolean;
  /** Pas encore mesurée : elle apparaît à sa place, sans glisser depuis une place supposée. */
  neuf: boolean;
  mesure: (element: HTMLDivElement | null) => void;
  enHierarchisation: boolean;
  /** Une bulle de la feuille ouverte : c'est elle que « hiérarchiser » numérote. */
  designable: boolean;
  rangEnAttente: number | null;
  /** Le calque de priorité, le même qu'en Carte (ticket 04 du chantier `priorites`). */
  horsDuCalque?: boolean;
  combienDansLeCalque?: { priorite: Priorite; combien: number } | null;
  /** Les touches de la carte (modes-de-la-carte, 02, `loupeSurLaBulle`), comme sur la Carte ; en Analyse, sa durée et son J-n. */
  loupe?: LoupeSurUneBulle;
  onEntrer: (id: string) => void;
  onFiche: (id: string) => void;
  onEtat: (id: string, etat: Etat) => void;
  onDesigner: (id: string) => void;
  onDeplier: (id: string) => void;
}) {
  const gestes = useDeuxGestes(
    () => onEntrer(bulle.id),
    () => onFiche(bulle.id)
  );

  const etat = bulle.etat;
  const commeUneIntention = bulle.jamaisOuverte && etat === 'a_faire';
  const posee = etat === 'fait';
  /* En réflexion (ticket 18) : une idée, pas une chose faite ; son étiquette la distingue. */
  const enReflexion = etat === 'sans_objet';

  /*
   * Ce qu'elle attend encore d'une autre bulle (une transversale qui retient). Une condition
   * acquise ne dit plus rien, comme sur la carte ; ce que ses propres bulles retiennent se
   * lit en les dépliant.
   */
  const attend = ceQuElleAttendSelon(bulle, 'hierarchie');

  const inerte = enHierarchisation && !designable;
  const classes = [
    'hier-noeud',
    axe ? 'hier-noeud--axe' : '',
    axe && dedans ? 'hier-noeud--dedans' : '',
    commeUneIntention ? 'hier-noeud--jamais-ouverte' : '',
    etat === 'en_attente_confirmation' ? 'hier-noeud--instruction' : '',
    posee ? 'hier-noeud--eteint' : '',
    enReflexion ? 'hier-noeud--reflexion' : '',
    enHierarchisation && designable ? 'hier-noeud--designable' : '',
    rangEnAttente !== null ? 'hier-noeud--numerote' : '',
    // Une seule raison de pâlir, et la carte pas encore mesurée reste invisible : le calque
    // ou le geste de hiérarchiser, écrits après dans le CSS, lui rendraient une opacité
    // (même défaut que les bulles cachées de la carte, `effacement.ts`).
    neuf
      ? 'hier-noeud--neuf'
      : horsDuCalque
        ? 'hier-noeud--hors-calque'
        : inerte
          ? 'hier-noeud--inerte'
          : '',
    loupe?.info ? 'hier-noeud--info' : '',
    loupe?.sansInfo ? 'hier-noeud--sans-info' : '',
    loupe?.terne ? 'hier-noeud--terne' : '',
    loupe?.allumee ? 'hier-noeud--allumee' : '',
    classeDeLaLueur(loupe?.analyse),
  ]
    .filter(Boolean)
    .join(' ');

  const dessin = attributsDeLHabit(habit);

  const corps = (
    <>
      <TitreSousLaLoupe classe="hier-noeud-titre" titre={bulle.titre} info={loupe?.info ?? null} />
      {enReflexion && <span className="etiquette-idee">{IDEE}</span>}
      {!enReflexion && loupe?.analyse && <DureeEnAnalyse classe="hier-noeud-duree" analyse={loupe.analyse} />}
      {combienDansLeCalque && (
        <span
          className="etiquette-calque"
          title={titreDuCalque(combienDansLeCalque.priorite, combienDansLeCalque.combien)}
        >
          {etiquetteDuCalque(combienDansLeCalque.priorite, combienDansLeCalque.combien)}
        </span>
      )}
      {attend.length > 0 && (
        <span className="hier-noeud-attend">
          Attend « {attend[0]!.titre} »
          {attend.length > 1 &&
            ` et ${attend.length - 1} ${attend.length - 1 > 1 ? 'autres' : 'autre'}`}
        </span>
      )}
      {/* Ce qu'elle contient se COMPTE d'un regard : un point par chose, jamais un nombre. */}
      <PointsDeContenu combien={bulle.nbEnfants} />
    </>
  );

  return (
    <div
      ref={mesure}
      className={dessin.classe ? `${classes} ${dessin.classe}` : classes}
      {...dessin.attributs}
      data-tuto="bulle-hierarchie"
      data-bulle={bulle.id}
      style={{
        ...dessin.style,
        left: place.x,
        top: place.y,
        width: place.largeur,
        ...(axe ? { ['--axe' as string]: axe } : {}),
      }}
    >
      {habit.objet && <SilhouetteDObjet objet={habit.objet} />}
      {rangEnAttente !== null && <span className="hier-noeud-attente">{rangEnAttente}</span>}
      <ReboursEnAnalyse analyse={loupe?.analyse} />
      {habitDuProjet && <MenuDeLHabit bulle={bulle} projet={habitDuProjet} horsDePortee={enHierarchisation || neuf} />}

      <button
        type="button"
        className="hier-noeud-pastille"
        onClick={() => onEtat(bulle.id, suivant(etat))}
        aria-label="changer l’état"
        disabled={enHierarchisation}
      >
        {commeUneIntention ? (
          <PastilleJamaisOuverte taille={15} />
        ) : (
          <PastilleEtat etat={etat} taille={15} />
        )}
      </button>

      {bulle.rang !== null && (
        <span className="hier-noeud-rang" aria-label={`ton rang : ${bulle.rang}`}>
          {bulle.rang}
        </span>
      )}

      {enHierarchisation ? (
        <button
          type="button"
          className="hier-noeud-corps"
          onClick={() => designable && onDesigner(bulle.id)}
          disabled={!designable}
        >
          {corps}
        </button>
      ) : (
        <button
          type="button"
          className="hier-noeud-corps"
          {...gestes}
          title={DEUX_GESTES}
        >
          {corps}
        </button>
      )}

      {bulle.nbEnfants > 0 && (
        <BoutonDeplier deplie={deplie} onDeplier={() => onDeplier(bulle.id)} />
      )}
    </div>
  );
}

/** La flèche ronde : `›` déplie les bulles dans la colonne suivante, `‹` les replie. */
export function BoutonDeplier({ deplie, onDeplier }: { deplie: boolean; onDeplier: () => void }) {
  const mot = deplie ? 'replier ses bulles' : 'déplier ses bulles';
  return (
    <button
      type="button"
      className="hier-noeud-deplier"
      onClick={onDeplier}
      aria-expanded={deplie}
      aria-label={mot}
      title={majuscule(mot)}
    >
      {deplie ? '‹' : '›'}
    </button>
  );
}
